import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const EventCard = ({ event }) => {
  const formatDateRange = (start, end) => {
    if (!start) return "TBD";
    const s = new Date(start + "T00:00:00");
    const opts = { month: "short", day: "numeric", year: "numeric" };
    if (!end || start === end) return s.toLocaleDateString("en-US", opts);
    const e = new Date(end + "T00:00:00");
    return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${e.toLocaleDateString("en-US", opts)}`;
  };

  const isUpcoming = event.end_date ? new Date(event.end_date + "T23:59:59") >= new Date() : true;

  return (
    <Link
      to={`/events/${event.slug}`}
      className="group block bg-zinc-900 border border-zinc-800 hover:border-orange-500 transition-all"
      data-testid={`event-card-${event.slug}`}
    >
      {event.banner_url ? (
        <div className="aspect-[21/9] overflow-hidden">
          <img src={event.banner_url} alt={event.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      ) : (
        <div className="aspect-[21/9] bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
          <Calendar className="w-12 h-12 text-zinc-700" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {event.sport && <span className="text-orange-500 text-xs font-bold uppercase">{event.sport}</span>}
          {isUpcoming ? (
            <span className="text-[10px] font-bold uppercase bg-green-600 text-white px-1.5 py-0.5">Upcoming</span>
          ) : (
            <span className="text-[10px] font-bold uppercase bg-zinc-700 text-zinc-300 px-1.5 py-0.5">Complete</span>
          )}
        </div>
        <h3 className="font-heading text-xl uppercase text-white group-hover:text-orange-400 transition-colors mb-2">
          {event.name}
        </h3>
        <div className="flex items-center gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>{formatDateRange(event.start_date, event.end_date)}</span>
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              <span>{event.location}</span>
            </div>
          )}
        </div>
        {event.broadcast_ids?.length > 0 && (
          <p className="text-xs text-zinc-600 mt-3">{event.broadcast_ids.length} game{event.broadcast_ids.length > 1 ? "s" : ""}</p>
        )}
      </div>
    </Link>
  );
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/api/events?include_unpublished=false`)
      .then(r => setEvents(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();
  const upcoming = events.filter(e => !e.end_date || new Date(e.end_date + "T23:59:59") >= now);
  const past = events.filter(e => e.end_date && new Date(e.end_date + "T23:59:59") < now);
  const filtered = filter === "upcoming" ? upcoming : filter === "past" ? past : events;
  const sports = [...new Set(events.map(e => e.sport).filter(Boolean))];

  return (
    <div className="min-h-screen bg-black pt-8 pb-16">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-zinc-500 text-sm uppercase tracking-wider mb-2">Antler Sports Network</p>
            <h1 className="font-heading text-4xl md:text-5xl uppercase text-white" data-testid="events-page-title">Events</h1>
            <p className="text-zinc-400 mt-2">Tournaments, showcases, and special events</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          {["all", "upcoming", "past"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${filter === f ? "bg-orange-500 text-black border-orange-500" : "border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"}`}
              data-testid={`events-filter-${f}`}
            >
              {f} {f === "upcoming" && upcoming.length > 0 ? `(${upcoming.length})` : f === "past" && past.length > 0 ? `(${past.length})` : ""}
            </button>
          ))}
          {sports.length > 1 && <span className="text-zinc-700 mx-2">|</span>}
          {sports.length > 1 && sports.map(s => (
            <span key={s} className="px-3 py-1 text-[10px] font-bold uppercase bg-zinc-900 text-zinc-500 border border-zinc-800">{s}</span>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-white/20 border-t-orange-500 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-zinc-800 mx-auto mb-4" />
            <h2 className="font-heading text-2xl uppercase text-zinc-500 mb-2">No Events</h2>
            <p className="text-zinc-600">Check back soon for upcoming tournaments and events.</p>
            <Link to="/" className="inline-block mt-6 px-6 py-2 border border-zinc-700 text-white text-sm uppercase font-bold hover:border-orange-500 transition-colors">
              <ChevronRight className="w-4 h-4 inline mr-1" /> Back Home
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {filtered.map(event => <EventCard key={event.id} event={event} />)}
          </div>
        )}
      </div>
    </div>
  );
}
